import { motion } from 'framer-motion'
import { Card } from '../components/ui/Card'
import { SparklesIcon, ShieldCheckIcon, ChartBarIcon, GlobeAltIcon } from '@heroicons/react/24/outline'

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.08 } } }
const item = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }

const values = [
  { icon: SparklesIcon, title: 'Innovation First', desc: 'We leverage cutting-edge AI and machine learning to deliver enterprise-grade intelligence that was previously only available to Fortune 500 companies.' },
  { icon: ShieldCheckIcon, title: 'Security & Trust', desc: 'Your data is encrypted at rest and in transit. We maintain SOC 2 compliance and implement row-level security across our entire platform.' },
  { icon: ChartBarIcon, title: 'Data-Driven Results', desc: 'Every feature is built around measurable outcomes. Our AI doesn\'t just analyze — it recommends specific actions backed by real data patterns.' },
  { icon: GlobeAltIcon, title: 'Global Accessibility', desc: 'Supporting multiple currencies, languages, and regional compliance requirements. IntelCore works wherever your business operates.' },
]

const team = [
  { name: 'IntelCore Engineering', role: 'Platform Development', desc: 'Building the infrastructure that powers real-time analytics, AI processing, and multi-channel automation for thousands of businesses.' },
  { name: 'IntelCore AI Research', role: 'Intelligence & Analytics', desc: 'Developing proprietary algorithms using Groq and Llama models to deliver actionable business insights in milliseconds.' },
  { name: 'IntelCore Security', role: 'Cybersecurity & Compliance', desc: 'Ensuring enterprise-grade protection through continuous vulnerability assessment, encryption, and compliance management.' },
]

export const AboutUs = () => {
  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-8 max-w-4xl mx-auto">
      <motion.div variants={item} className="text-center">
        <h1 className="text-3xl font-bold text-metallic-900">About IntelCore</h1>
        <p className="text-metallic-500 mt-2 max-w-2xl mx-auto">
          IntelCore is an AI-powered Enterprise Intelligence platform that empowers companies, professionals, and entrepreneurs with the tools they need to make smarter decisions, automate operations, and scale their businesses.
        </p>
      </motion.div>

      <motion.div variants={item}>
        <Card className="bg-gradient-to-br from-primary-50 to-white border-primary-200">
          <h2 className="text-xl font-semibold text-metallic-900 mb-4">Our Mission</h2>
          <p className="text-metallic-600 leading-relaxed">
            To democratize enterprise-grade business intelligence and automation. We believe every business — from solo entrepreneurs to large corporations — deserves access to AI-powered analytics, marketing automation, CRM, and security tools that drive real growth. Our platform processes millions of data points to deliver insights that matter, transforming raw data into strategic advantage.
          </p>
        </Card>
      </motion.div>

      <motion.div variants={item}>
        <h2 className="text-xl font-semibold text-metallic-900 mb-4">What We Offer</h2>
        <p className="text-metallic-600 leading-relaxed mb-6">
          IntelCore is a comprehensive business platform offering 9 integrated services under one roof:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {['Enterprise Intelligence', 'Web Design', 'Marketing Automation', 'AI Chat & Voice Agents', 'CRM System', 'Branding', 'Vulnerability Assessment', 'Mobile App Development', 'Google Maps Visibility'].map((service) => (
            <div key={service} className="p-3 bg-metallic-50 rounded-xl text-sm font-medium text-metallic-700 text-center">
              {service}
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div variants={item}>
        <h2 className="text-xl font-semibold text-metallic-900 mb-4">Our Values</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {values.map((v) => (
            <Card key={v.title} hover>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <v.icon className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-metallic-900">{v.title}</h3>
                  <p className="text-sm text-metallic-500 mt-1">{v.desc}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </motion.div>

      <motion.div variants={item}>
        <h2 className="text-xl font-semibold text-metallic-900 mb-4">Our Team</h2>
        <div className="space-y-4">
          {team.map((t) => (
            <Card key={t.name}>
              <h3 className="font-semibold text-metallic-900">{t.name}</h3>
              <p className="text-sm text-primary-600 font-medium">{t.role}</p>
              <p className="text-sm text-metallic-500 mt-2">{t.desc}</p>
            </Card>
          ))}
        </div>
      </motion.div>

      <motion.div variants={item}>
        <Card className="bg-gradient-to-r from-primary-500 to-primary-700 text-white text-center py-8">
          <h2 className="text-2xl font-bold mb-2">Ready to Transform Your Business?</h2>
          <p className="text-primary-100 mb-4">Join 500+ companies already using IntelCore to make smarter decisions.</p>
          <a href="/" className="inline-block px-6 py-3 bg-white text-primary-600 rounded-xl font-semibold hover:bg-primary-50 transition-colors">
            Get Started Free
          </a>
        </Card>
      </motion.div>
    </motion.div>
  )
}
